import moment from 'moment-timezone'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  createSequence,
  getSequences,
  editSequence,
  editTrigger,
  createTrigger,
  editEmailTemplate,
  createEmailTemplate,
  createPushNotificationTemplate,
  editPushNotificationTemplate,
  deletePushNotificationTemplate,
  createSMSNotificationTemplate,
  editSMSNotificationTemplate,
  deleteSMSNotificationTemplate,
  createAction,
  editAction,
  deleteTrigger,
  deleteAction,
  deleteEmailTemplate,
  editSchedule,
  createSchedule,
  deleteSchedule,
} from '../../../../gql'
import { PATHS } from '../../../../../../../../../Config/Routing/paths'
import { calculateTargetValue, calculateTimeDelay } from '../../../../utils'

const saveSequence = async ({ sequence, cityId, stage }) => {
  let sequenceId = sequence.id
  let errors = []
  try {
    let sequenceName = sequence.name

    const response = await (await getSequences({
      cityId,
      name: sequenceName,
    })).json()

    const existingSeqs = JSON.parse(response.data.getSequences)

    if (existingSeqs.length > 0 && existingSeqs[0].id !== sequenceId) {
      errors.push(
        'A sequence with this name already exists, please use another name for the new sequence.',
      )
      return { sequenceId, errors }
    } else {
      if (sequenceId.includes('temp')) {
        const seqResponse = await createSequence({
          cityId,
          stage,
          name: sequence.name,
          username: sequence.owner,
        })
        const { data: seqDataRaw } = await seqResponse.json()
        const seqData = JSON.parse(seqDataRaw.createSequence)
        sequenceId = seqData.id
      } else {
        await editSequence({
          id: sequenceId,
          name: sequenceName,
          stage: stage,
        })
      }
    }
  } catch (e) {
    errors.push(e)
  } finally {
    return { sequenceId, errors }
  }
}

const saveSchedule = async ({
  sequence,
  sequenceId,
  initialSequence,
  cityTimezone,
}) => {
  let errors = []
  let schedulesSaved
  try {
    schedulesSaved = await Promise.allSettled([
      ...sequence.DateSequenceTemplate.map(async schedule => {
        const date = moment(schedule.start_date).tz(cityTimezone)
        const utcOffsetInMinutes = date.utcOffset()
        const startDate = moment(schedule.start_date)
          .add(-utcOffsetInMinutes, 'minutes')
          .format('YYYY-MM-DD HH:mm:ss')
        if (!schedule.id.includes('temp')) {
          await editSchedule({
            id: schedule.id,
            sequenceId,
            startDate,
          })
        } else {
          await createSchedule({
            sequenceId,
            startDate,
          })
        }
      }),
    ])
    if (schedulesSaved.some(result => result.status === 'rejected')) {
      throw new Error('Error saving schedule')
    }
    const deletedSchedule = initialSequence.DateSequenceTemplate.find(
      schedule =>
        !schedule.id.includes('temp') &&
        !sequence.DateSequenceTemplate.find(t => t.id === schedule.id),
    )
    if (deletedSchedule) {
      await deleteSchedule(Number(deletedSchedule.id))
    }
  } catch (e) {
    errors =
      schedulesSaved.length > 0
        ? schedulesSaved.map(result => result.reason.message)
        : [e]
  } finally {
    return { errors }
  }
}

const saveTrigger = async ({ sequence, sequenceId, initialSequence }) => {
  let errors = []
  let triggersSaved
  try {
    triggersSaved = await Promise.all([
      ...sequence.Trigger.map(async trigger => {
        if (!trigger.id.includes('temp')) {
          await editTrigger({
            triggerId: trigger.id,
            triggerType: trigger.triggerType.name,
            condition: trigger.triggerType.conditionType
              ? trigger.triggerType.conditionType.operation
              : '',
            constant: String(
              calculateTargetValue(sequence, trigger.target_value),
            ),
          })
        } else {
          await createTrigger({
            sequenceId,
            triggerType: trigger.triggerType.name,
            condition: trigger.triggerType.conditionType
              ? trigger.triggerType.conditionType.operation
              : '',
            constant: String(
              calculateTargetValue(sequence, trigger.target_value),
            ),
          })
        }
      }),
    ])
    const deletedTrigger = initialSequence.Trigger.find(
      trigger =>
        !trigger.id.includes('temp') &&
        !sequence.Trigger.find(t => t.id === trigger.id),
    )
    if (deletedTrigger) {
      deleteTrigger({ triggerId: deletedTrigger.id })
    }
  } catch (e) {
    errors.push(e)
  } finally {
    return { triggersSaved, errors }
  }
}

const saveActionAndTemplates = async ({
  sequence,
  sequenceId,
  cityId,
  initialSequence,
}) => {
  let errors = []
  let actionsSaved
  try {
    const deletedAction = initialSequence.Action.filter(
      action =>
        !action.id.includes('temp') &&
        !sequence.Action.find(a => a.id === action.id),
    )

    if (deletedAction && Array.isArray(deletedAction) && deletedAction.length) {
      await Promise.all([
        ...deletedAction.map(async action => {
          await deleteAction({ actionId: action.id })
          if (action.actionType.name === 'EMAIL' && action.template.id) {
            await deleteEmailTemplate({ id: action.template.id })
          } else if (
            action.actionType.name === 'NOTIFICATION' &&
            action.template.id
          ) {
            await deletePushNotificationTemplate(action.template.id)
          } else if (action.actionType.name === 'SMS' && action.template.id) {
            await deleteSMSNotificationTemplate(action.template.id)
          }
        }),
      ])
    }

    actionsSaved = await Promise.all([
      ...sequence.Action.map(async action => {
        const actionId = action.id
        const commId = String(action.template.id)
        let templateId
        if (action.actionType.name === 'EMAIL') {
          const {
            name,
            subject,
            sender,
            body,
            cc,
            bcc,
            files,
            deletedFilesIds,
            customDynamicValues,
          } = action.template
          let emailResponse

          if (!commId.includes('temp')) {
            emailResponse = await editEmailTemplate({
              id: commId,
              name: name,
              subject,
              sender,
              body: JSON.stringify(body),
              cc,
              bcc,
              files: files
                ? files
                    .filter(f => !f.fileUrl)
                    .map(f => ({ fileName: f.fileName, file: f.file }))
                : null,
              deletedFilesIds,
              customDynamicValues,
            })
            const { data: emailData } = await emailResponse.json()

            templateId = !!emailData && emailData['editEmailTemplate']['id']
          } else {
            emailResponse = await createEmailTemplate({
              cityId,
              name,
              subject,
              sender,
              body: JSON.stringify(body),
              cc,
              bcc,
              files: files
                ? files.map(f => ({ fileName: f.fileName, file: f.file }))
                : null,
              customDynamicValues,
            })
            const { data: emailData } = await emailResponse.json()

            templateId = !!emailData && emailData['createEmailTemplate']['id']
          }
        } else if (action.actionType.name === 'NOTIFICATION') {
          const {
            name,
            title,
            body,
            route,
            customDynamicValues,
          } = action.template
          let notificationResponse

          if (!commId.includes('temp')) {
            notificationResponse = await editPushNotificationTemplate({
              id: commId,
              name,
              body,
              title,
              route,
              customDynamicValues,
            })
            const { data: notificationData } = await notificationResponse.json()
            templateId =
              !!notificationData &&
              notificationData['editPushNotificationTemplate']['id']
          } else {
            notificationResponse = await createPushNotificationTemplate({
              name,
              body,
              title,
              route,
              customDynamicValues,
            })
            const { data: notificationData } = await notificationResponse.json()
            templateId =
              !!notificationData &&
              notificationData['createPushNotificationTemplate']['id']
          }
        } else if (action.actionType.name === 'SMS') {
          const { name, body, customDynamicValues } = action.template
          let smsResponse

          if (!commId.includes('temp')) {
            smsResponse = await editSMSNotificationTemplate({
              id: commId,
              name,
              body: JSON.stringify(body),
              customDynamicValues,
            })
            const { data: smsData } = await smsResponse.json()
            templateId =
              !!smsData && smsData['editSMSNotificationTemplate']['id']
          } else {
            smsResponse = await createSMSNotificationTemplate({
              name,
              body: JSON.stringify(body),
              customDynamicValues,
            })
            const { data: smsData } = await smsResponse.json()
            templateId =
              !!smsData && smsData['createSMSNotificationTemplate']['id']
          }
        }

        if (!actionId.includes('temp')) {
          await editAction({
            actionId,
            timing: String(
              calculateTimeDelay(
                sequence,
                action.time_before,
                action.time_delay,
              ),
            ),
          })
        } else {
          await createAction({
            actionTypeId: action.actionType.id,
            sequenceId,
            templateId,
            timing: String(
              calculateTimeDelay(
                sequence,
                action.time_before,
                action.time_delay,
              ),
            ),
          })
        }
      }),
    ])
  } catch (e) {
    errors.push(e)
  } finally {
    return { actionsSaved, errors }
  }
}

export const useSequenceSubmit = (seqStatus, cityTimezone) => {
  const history = useHistory()
  const [submitLoading, setSubmitLoading] = useState(false)
  const [submitErrors, setSubmitErrors] = useState()
  const [savedSequenceId, setSavedSequenceId] = useState()

  const onSubmit = async ({
    sequence,
    cityId,
    stage,
    url,
    initialSequence,
  }) => {
    setSubmitLoading(true)
    setSubmitErrors(undefined)

    try {
      let sequenceId = sequence.id

      if (
        sequence.id.includes('temp') ||
        sequence.name !== initialSequence.name ||
        stage !== initialSequence.stage
      ) {
        const {
          sequenceId: sequenceIdResponse,
          errors: saveSequenceErrors,
        } = await saveSequence({
          sequence,
          cityId,
          stage,
        })
        if (saveSequenceErrors.length) {
          setSubmitLoading(false)
          setSubmitErrors(saveSequenceErrors)
          return
        }
        setSavedSequenceId(sequenceIdResponse)
        sequenceId = sequenceIdResponse
      }

      if (!sequenceId.includes('temp')) {
        const { errors: saveScheduleErrors } = await saveSchedule({
          sequence,
          sequenceId,
          initialSequence,
          cityTimezone,
        })
        if (saveScheduleErrors.length) {
          setSubmitLoading(false)
          return
        }

        const { errors: saveTriggerErrors } = await saveTrigger({
          sequence,
          sequenceId,
          initialSequence,
        })
        if (saveTriggerErrors.length) {
          setSubmitLoading(false)
          setSubmitErrors(saveTriggerErrors)
          return
        }

        const { errors: saveActionErrors } = await saveActionAndTemplates({
          sequence,
          sequenceId,
          cityId,
          initialSequence,
        })
        if (saveActionErrors.length) {
          setSubmitLoading(false)
          setSubmitErrors(saveActionErrors)
          return
        }

        if (sequenceId) {
          history.push(
            url +
              PATHS.cities.ambassadorsEmails +
              '/' +
              sequenceId +
              PATHS.cities.edit,
            { from: 'create', seqStatus },
          )
        }
      }
    } catch (e) {
      setSubmitErrors(e)
    } finally {
      setSubmitLoading(false)
    }
  }
  return { onSubmit, submitErrors, submitLoading, savedSequenceId }
}
